(ns ui.editor2.sidebar-media
  (:require [clojure.string :as str]
            [datascript.core :as ds]
            [ui.editor2.common :as common]
            [ui.editor2.extension-points :as extension-points]))

(defn ai-badge
  "Marks AI generated assets. A click opens the model dialog prefilled
   with the asset's recipe (regenerate workflow), hovering shows the
   `ai-tooltip` with the used model, prompt and parameters."
  [asset]
  (let [data (:asset/data asset)]
    (when (:ai/category data)
      [:div.absolute.top-1.right-1.z-20
       [:button.flex.items-center.justify-center.rounded-full.bg-gray-900.bg-opacity-80.p-1.text-amber-300.hover:text-amber-200
        {:type "button"
         :on {:click [[:js/stop-propagation]
                      [:openrouter/hide-asset-tooltip]
                      [:openrouter/regenerate {:asset-uuid (:asset/uuid asset)}]]
              :mouseenter [[:openrouter/show-asset-tooltip (:asset/uuid asset)]]
              :mouseleave [[:openrouter/hide-asset-tooltip]]}}
        ;; Sparkles icon:
        [:svg
         {:xmlns "http://www.w3.org/2000/svg"
          :viewBox "0 0 20 20"
          :fill "currentColor"
          :class ["w-3.5" "h-3.5" "pointer-events-none"]}
         [:path
          {:fill-rule "evenodd"
           :clip-rule "evenodd"
           :d "M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM16 1a.75.75 0 0 1 .728.568l.258 1.036a2.63 2.63 0 0 0 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258a2.63 2.63 0 0 0-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.63 2.63 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.63 2.63 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 16 1Z"}]]]])))

(defn ai-tooltip
  "The info box for the hovered AI badge. Rendered with a fixed position
   at the coordinates captured on hover, so it escapes the sidebar's
   clipping scroll container and paints above the surrounding UI."
  [state]
  (when-let [{:keys [asset-uuid x y]} (get-in state [:editor2 :ai-sidebar :tooltip])]
    (let [asset (ds/entity (get-in state [:editor2 :db])
                           [:asset/uuid asset-uuid])
          data (:asset/data asset)]
      (when (:ai/category data)
        [:div.rounded-md.bg-gray-900.p-2.text-left.shadow-lg
         {:style {:position "fixed"
                  :left (str (max 8 (- x 224)) "px")
                  :top (str (+ y 6) "px")
                  :width "14rem"
                  :z-index 100
                  :pointer-events "none"}}
         [:p.text-xs.font-semibold.text-white.break-all
          (:ai/model-id data)]
         (when-let [prompt (or (:ai/prompt data)
                               (:ai/text data))]
           [:p.text-xs.text-slate-300.mt-1
            {:style {:display "-webkit-box"
                     :-webkit-line-clamp "5"
                     :-webkit-box-orient "vertical"
                     :overflow "hidden"}}
            prompt])
         (let [params (remove nil?
                              [(some->> (:ai/aspect-ratio data)
                                        (str "Aspect ratio: "))
                               (some->> (:ai/voice data)
                                        (str "Voice: "))
                               (some->> (:ai/resolution data)
                                        (str "Resolution: "))
                               (some->> (:ai/duration data)
                                        (str "Duration: "))])]
           (when (seq params)
             [:p.text-xs.text-slate-400.mt-1
              (str/join " · " params)]))
         [:p.text-xs.text-slate-500.mt-1
          "Click to edit the prompt and regenerate"]]))))

(defn q-asset-uuids
  [db]
  (ds/q
    '[:find
      [?asset-uuid ...]
      :where
      [?asset :asset/uuid ?asset-uuid]]
    db))

(defn get-assets
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])]
    (map (fn [asset-uuid]
           (ds/entity db
                      [:asset/uuid asset-uuid]))
         (q-asset-uuids db))))

(defn panel
  [state]
  ;; panel-grid-list

  (let [all-assets (get-assets state)
        filtered-assets (filter (fn [item]
                                  (not (#{:hashtag-sticker
                                          :mention-sticker
                                          :poll-sticker
                                          :link-sticker
                                          :location-sticker
                                          :countdown-sticker
                                          :question-sticker
                                          :quiz-sticker
                                          :emoji-slider-sticker
                                          :text}
                                        (:asset/type item))))
                                all-assets)]
    (if (empty? filtered-assets)
      [:div.flex.flex-col.items-center.justify-center.w-full.h-full
       [:svg.size-6.mb-4
        {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "1.5"
         :stroke "currentColor"}
        [:path
         {:stroke-linecap "round"
          :stroke-linejoin "round"
          :d "M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"}]]
       [:p.mb-3.text-center
        "Your collection is empty"]
       [:p.text-center.text-slate-400
        "Enhance your content by adding videos, images, gifs and more."]
       [:button.flex.items-center.justify-center.bg-gray-500.rounded-md.p-2.mt-5
        {:type "button"
         :on {:click [[:dom/click (str "#" common/media-upload-input-id)]]}}
        [:svg
         {:xmlns "http://www.w3.org/2000/svg",
          :fill "none",
          :viewBox "0 0 24 24",
          :stroke-width "1.5",
          :stroke "currentColor",
          :class ["size-6"]}
         [:path
          {:stroke-linecap "round",
           :stroke-linejoin "round",
           :d
           "M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"}]]
        [:span.hidden.lg:block.ml-2
         "Add Media"]]]
      [:div
       {:class ["flex" "flex-col" "gap-2" "w-full"]}
       (ai-tooltip state)
       ;; panel-grid-list__header
       [:div
        {:class ["flex" "items-center" "justify-between" "w-full"]}
        [:span
         {:class ["text-xs" "font-medium"]}
         "Content of your Story"]]

       ;; panel-grid-list__content
       [:div
        {:class ["grid" "gap-2"]
         :style {:grid-template-columns "repeat(2, 1fr)"}}

        (for [asset filtered-assets]
          ;; media-card
          [:div
           {:class ["flex" "flex-col" "gap-1.5" "w-full" "relative" "cursor-pointer"]
            :on {:click [[:editor2/add-asset-as-clip (:asset/uuid asset)]]}}
           ;; thumbnail
           [:div.relative
            {:class ["rounded-lg" "overflow-hidden" "relative" "w-full" "flex" "justify-center" "bg-gray-900"]
             :style {:filter "blur(0)"
                     :height "3.375rem"}}
            [:img
             {:class ["h-full" "object-contain" "max-w-full" "w-full" "pointer-events-none"]
              :src (extension-points/asset-thumbnail
                     {:asset asset
                      :state state})}]
            (when-let [progress (get-in state [:upload-manager
                                               :uploads
                                               (:asset/uuid asset)
                                               :progress])]
              (when (< progress 1)
                [:div.flex.items-center.gap-x-3.whitespace-nowrap.absolute.inset-x-0.bottom-0
                 [:div.flex.w-full.h-2.rounded-full.overflow-hidden
                  [:div.flex.flex-col.justify-center.rounded-full.overflow-hidden.bg-gray-400.text-xs.text-white.text-center.whitespace-nowrap.transition.duration-500
                   {:style {:width (str (* 100.0 progress)
                                        "%")}}]]
                 [:div.w-6.text-end
                  [:span.text-xs.text-white (str (int (* 100.0 progress))
                                                 "%")]]]))]
           (ai-badge asset)
           ;; media-card__title
           [:p
            {:class ["text-xs" "font-medium" "mb-2" "overflow-hidden" "text-ellipsis" "whitespace-nowrap" "max-w-24"]}
            (:asset/name asset)]
           ])

        ]])))
